import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField } from '@mui/material';
import { ReactComponent as HubIcon } from '../../../../../images/icons/hub_icon.svg';
import { ReactComponent as DeleteSuccessIcon } from '../../../../../images/icons/delete_success_icon.svg';
import { ReactComponent as DeleteFailedIcon } from '../../../../../images/icons/delete_failed_icon.svg';
import NewModal from '../../../../Shared/NewModal';
import { dimGrey } from '../../../../../mainTheme';
import {
  clearDeviceActionStatus,
  deleteDatabase,
} from '../../../../../store/devices/actionCreators';
import { useDispatch, useSelector } from 'react-redux';
import { DevicesStore } from '../../../../../store/devices/deviceTypes';
import { RootState } from '../../../../../store';

const typography = {
  heading: {
    fontWeight: 500,
    fontSize: '22px',
    lineHeight: '30px',
    letterSpacing: '0.36px',
  },
  subText: {
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '22px',
    letterSpacing: '0.2px',
    color: dimGrey,
  },
} as const;

interface DeleteDatabaseDialogProps {
  onClose: () => void;
  deviceId: number;
}

interface StatusViewProps {
  icon: React.ReactElement;
  title: string;
  message: string;
}

const StatusView: React.FC<StatusViewProps> = ({ icon, title, message }) => (
  <Box display="flex" flexDirection="column" alignItems="center">
    {icon}
    <Typography sx={{ ...typography.heading, mt: 2 }}>{title}</Typography>
    <Typography sx={{ ...typography.subText, textAlign: 'center' }}>
      {message}
    </Typography>
  </Box>
);

export const DeleteDatabaseDialog: React.FC<DeleteDatabaseDialogProps> = ({
  onClose,
  deviceId,
}) => {
  const { actionStatus } = useSelector<
    RootState,
    { actionStatus: DevicesStore['actionStatus'] }
  >((state) => ({
    actionStatus: state.devices.actionStatus,
  }));

  const dispatch = useDispatch();
  const [step, setStep] = useState<'confirm' | 'success' | 'failed'>('confirm');
  const [verificationText, setVerificationText] = useState('');

  const handleVerification = () => {
    if (verificationText.toLowerCase() === 'delete') {
      dispatch(deleteDatabase(deviceId));
    }
  };

  useEffect(() => {
    if (actionStatus.type === 'delete_hub_db') {
      if (actionStatus.success) {
        setStep('success');
      } else if (actionStatus.success === false) {
        setStep('failed');
      }
    }
  }, [actionStatus]);

  const handleReset = () => {
    dispatch(clearDeviceActionStatus());
    setStep('confirm');
    setVerificationText('');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const renderConfirmView = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <HubIcon />
      <Typography sx={typography.heading}>Delete database</Typography>
      <Typography sx={typography.subText}>
        This will delete the database including patient and medication
        information. This action will not unassign an owner from the Hub. Once
        complete, the Hub will reboot.
      </Typography>
      <Typography sx={{ ...typography.subText, fontWeight: 700, ml: 2 }}>
        Type the word &quot;delete&quot; to confirm.
      </Typography>
      <TextField
        fullWidth
        value={verificationText}
        onChange={(e) => setVerificationText(e.target.value)}
        placeholder="Type 'delete'"
        variant="outlined"
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
          },
        }}
      />
    </Box>
  );

  const renderContent = () => {
    switch (step) {
      case 'confirm':
        return renderConfirmView();
      case 'success':
        return (
          <StatusView
            icon={<DeleteSuccessIcon />}
            title="Database deleted successfully"
            message="The Hub will reboot shortly."
          />
        );
      case 'failed':
        return (
          <StatusView
            icon={<DeleteFailedIcon />}
            title="Failed to delete database"
            message="Please try again at a later time."
          />
        );
    }
  };

  const actionConfig = {
    success: { text: 'OK', onClick: handleClose },
    failed: { text: 'Try again', onClick: handleReset },
    confirm: { text: 'Delete', onClick: handleVerification },
  };

  return (
    <NewModal
      showCloseX
      title="Delete database"
      onClose={handleClose}
      maxWidth="xs"
      actions={{
        primaryAction: {
          ...actionConfig[step],
          disabled:
            step === 'confirm' && verificationText.toLowerCase() !== 'delete',
        },
        secondaryAction:
          step === 'confirm'
            ? {
                text: 'Back',
                onClick: handleClose,
              }
            : undefined,
      }}
    >
      {renderContent()}
    </NewModal>
  );
};
